/**
 * @author NikolaV
 */

import { AlignmentsLayer } from '@openlayers/alignments/AlignmentsLayer';
import { ModelsLayer } from '@openlayers/models/ModelsLayer';
import { featuresExtent, latLonToEN } from '@openlayers/ol-utils';
import { PipesLayer } from '@openlayers/pipes/PipesLayer';
import { GtPointsLayer } from '@openlayers/points/GtPointsLayer';
import { StringlinesLayer } from '@openlayers/stringlines/StringlinesLayer';
import { ModelsPanelToolbar } from '@pages/map/ui/models/ModelsPanelToolbar';
import { Events, EventsGlobal } from '@shared/Events';
import { TreeSelectionFeedback } from '@shared/map/TreeSelectionFeedback';
import { Tree, TreeNode } from '@shared/ui-native/Tree';
import { Catch } from '@shared/ui-react/Catch';
import { ReactUtils } from '@shared/ui-react/ReactUtils';
import { PageStateUtils } from '@shared/user-state/PageStateUtils';
import { countsIncludesModels } from '@shared/utils/utils';
import { makeObservable, observable } from 'mobx';
import * as olExtent from 'ol/extent';
import * as React from 'react';
import { Model } from '../model';
import { StructureLayer } from '../StructureLayer';
import { FileTreeService } from '@shared/services/FileTreeService';
import { ModelService } from '@shared/models/model-service';
import { TekPointsLayer } from '@shared/openlayers/points/TekPointsLayer';

interface IMapLayers {
    models?: ModelsLayer;
    pipes?: PipesLayer;
    stringlines?: StringlinesLayer;
    points?: GtPointsLayer;
    tekPoints?: TekPointsLayer;
    alignments?: AlignmentsLayer;
    structures?: StructureLayer;
}

export class ModelsPanel implements IStateful<IMapPageStateModelsPanel> {
    tree: Tree = null;
    isEnabled = true;

    activeModels: Model[];
    modelsInLoading: Set<number>;
    queueFocusIdOrUuid: number | string;
    preloadTree = new Tree({
        // tree used to load models from live saved state, before full tree is loaded
        renderView: false,
        showCheckboxes: true,
    });

    @observable settings = {
        layerOpacity: 100,
    };

    constructor(public panelWrapper: HTMLElement, private readonly treeData: Promise<ITreeData>, private readonly layers: IMapLayers) {
        makeObservable(this);

        // render toolbar
        ReactUtils.safeRender(
            <Catch>
                <ModelsPanelToolbar pointsLayer={this.layers.points} settings={this.settings} onLayerOpacityChange={this.setLayerOpacity} />
            </Catch>,
            document.querySelector('#js-app__models-panel__toolbar-wrapper')
        );

        this.activeModels = [];
        this.modelsInLoading = new Set();
        this.renderSelectionFeedback();

        this.preloadTree.initAndRender({
            id: kuura.getProjectId(),
            uuid: kuura.getProjectUuid(),
            models: [],
        } as any);

        if (treeData) {
            this.initModelsTree();
        }
    }

    enabled() {
        return this.isEnabled;
    }

    public checkIds(ids: number[] = []) {
        if (!this.tree) return;

        ids.forEach(id => {
            const node = this.tree.getById(id);
            if (node) {
                node.setChecked(1);
            }
        });

        this.tree.calculateCheckboxes();

        this.tree.each(c => {
            if (c.state.checked > 0) {
                c.setExpanded(true);
            }
        });
    }

    public checkUuids(uuids: string[] = []) {
        if (!this.tree) return;

        uuids.forEach(uuid => {
            const node = this.tree.getByUuid(uuid);
            if (node) {
                node.setChecked(1);
            }
        });

        this.tree.calculateCheckboxes();

        this.tree.each(c => {
            if (c.state.checked > 0) {
                c.setExpanded(true);
            }
        });
    }

    public getNodeExtent(node: TreeNode): olExtent.Extent {
        let extent = olExtent.createEmpty();
        if (!node) return extent;
        if (!node.ajaxData.hasOwnProperty('layerCounts') || !node.ajaxData.layerCounts) return extent;
        const counts: any = node.ajaxData.layerCounts;
        const id = node.id;
        if (counts.pipeNetworks > 0) {
            const features = this.layers.pipes.getFeaturesBySource({ modelId: id });
            olExtent.extend(extent, featuresExtent(features));
        }

        if (counts.alignments > 0) {
            const feature = this.layers.alignments.getFeatureById(node.id);
            if (feature) {
                olExtent.extend(extent, feature.getGeometry().getExtent());
            } else {
                // there can be multiple alignment in a file with id format {file_id}_{index}
                for (let index = 0; index < counts.alignments; index++) {
                    const feature = this.layers.alignments.getFeatureById(node.id + '_' + index);
                    if (feature) {
                        olExtent.extend(extent, feature.getGeometry().getExtent());
                    }
                }
            }
        }

        if (counts.triangleMeshes > 0 || counts.stringlines > 0) {
            const features = this.layers.models.getFeaturesBySource({ modelId: id });
            olExtent.extend(extent, featuresExtent(features));

            const model = this.activeModels.find(m => m.uuid === node.uuid);

            if (model && model.extent) {
                olExtent.extend(extent, model.extent);
            }
        }

        if (counts.breaklineLayers > 0) {
            const stringlines = this.layers.stringlines.getFeaturesBySource({ modelId: id });

            olExtent.extend(extent, featuresExtent(stringlines));
        }

        if (counts.pointsLayers > 0) {
            const features = this.layers.points.getFeaturesBySource({ modelId: id });
            extent = featuresExtent(features);
            const tekPoints = this.layers.tekPoints?.getFeaturesBySource({ modelId: id }) || [];
            const tekPointsExtent = featuresExtent(tekPoints);
            olExtent.extend(extent, tekPointsExtent);
        }

        if (counts.features > 0) {
            const models = this.layers.models.getFeaturesBySource({ modelId: id });
            const stringlines = this.layers.stringlines.getFeaturesBySource({ modelId: id });
            const points = this.layers.points.getFeaturesBySource({ modelId: id });
            const tekPoints = this.layers.tekPoints?.getFeaturesBySource({ modelId: id }) || [];

            const modelsExtent = featuresExtent(models);
            const stringlinesExtent = featuresExtent(stringlines);
            const pointsExtent = featuresExtent(points);
            const tekPointsExtent = featuresExtent(tekPoints);
            olExtent.extend(extent, modelsExtent);
            olExtent.extend(extent, stringlinesExtent);
            olExtent.extend(extent, pointsExtent);
            olExtent.extend(extent, tekPointsExtent);
        }

        return extent;
    }

    public clearSelection = () => {
        this.layers.models.layerSource.clear();
        if (this.layers.models.onFeaturesCollectionChange) {
            this.layers.models.onFeaturesCollectionChange(null, null);
        }
        this.layers.points.layerSource.clear();
        this.layers.stringlines.layerSource.clear();
        this.layers.pipes.layerSource.clear();

        this.tree.each(c => {
            c.setChecked(0, false, false);
        }, true);
        this.layers.alignments.layerSource.clear();

        this.tree.calculateCheckboxes();
    };

    public focusItem(idOrUuid: any) {
        if (!this.tree) {
            this.queueFocusIdOrUuid = idOrUuid;
            return;
        }
        const item = $.isNumeric(idOrUuid) ? this.tree.getById(idOrUuid) : this.tree.getByUuid(idOrUuid) ?? this.tree.getByHeadUuid(idOrUuid);
        if (item) {
            item.setSelected(true, true, true);
            item.scrollTo();
        }
    }

    public openInFilesPage(id: number) {
        const item = this.tree.getById(id);
        if (item && item.parent) {
            window.location.href = window.location.origin + kuura.getContextPath() + '/models.htm#/' + item.parent.id + '/' + id;
        }
    }

    public checkItem(id: number) {
        if (!this.tree) return;

        const treeItem = this.tree.getById(id);
        if (treeItem) {
            treeItem.set('checked', 2);
            this.tree.calculateCheckboxes();
        }
    }

    public getChecked(): TreeNode[] {
        if (!this.tree) return [];

        return this.tree
            .getChecked()
            .filter(c => c.isFile())
            .map(c => c);
    }

    public getCheckedHeadUuids(): string[] {
        if (!this.tree) return [];

        return this.tree
            .getChecked()
            .filter(c => c.isFile())
            .map(c => c.headUuid);
    }

    public getCheckedFileIds(): number[] {
        if (!this.tree) return [];

        return this.tree
            .getChecked()
            .filter(c => c.isFile())
            .map(c => c.id);
    }

    public getCheckedFileNames(): string[] {
        if (!this.tree) return [];

        return this.tree
            .getChecked()
            .filter(c => c.isFile())
            .map(c => c.text);
    }

    public setLayerOpacity = (opacity: number) => {
        this.settings.layerOpacity = opacity;
        this.layers.models.setLayerOpacity(opacity);
        this.layers.alignments.setLayerOpacity(opacity);
        this.layers.stringlines.setLayerOpacity(opacity);
        this.layers.points.setLayerOpacity(opacity);
        this.layers.pipes.setLayerOpacity(opacity);
    };

    initModelsTree() {
        this.panelWrapper.classList.add('overlay-loading');
        this.treeData.then(treeData => {
            this.tree = new Tree({
                rootEl: $('#js-app__models-panel__tree-wrapper'),
                showRoot: false,
                showCheckboxes: true,
                selectionCheckbox: false,
                checkOnHighlightSelected: true,
                hideNonPresentable: true,
                propagateCheckbox: 'first_level_files',
                hideEmptyFoldersAlways: true,
                showContextMenuInfo: true,
                storageKey: 'map-models-panel',

                childFilter: c => {
                    if (c.tolerance) return true; // is folder
                    if (kuuraModel.isMap(c) && !kuuraModel.isModel(c)) return false; // ?
                    return kuuraModel.hasAnyDrawableLayer(c);
                },
            });

            this.tree.on('check', (e, data) => {
                data.changed.forEach(node => {
                    // checked file
                    if (node.isFile()) {
                        if (node.state.checked == 1) {
                            this.handleCheckedFileNode(node);
                        } else {
                            this.handleUncheckedFile(node);
                        }
                    }
                    this.renderSelectionFeedback();
                });
            });

            // selecting tree item will fit that item into view
            this.tree.on('select', (e, data) => {
                const node: TreeNode = data.node;
                let extent: olExtent.Extent;

                if (node.isFolder()) {
                    extent = olExtent.createEmpty();

                    node.each(child => {
                        const nodeExtent = this.getNodeExtent(child);
                        olExtent.extend(extent, nodeExtent);
                    }, true);
                } else if (node.isLink()) {
                    const targetNode = this.tree.getByUuid(node.ajaxData.modelUuid);
                    extent = this.getNodeExtent(targetNode);
                } else {
                    extent = this.getNodeExtent(node);
                }
                Events.trigger(EventsGlobal.MAP_FIT_EXTENT, { extent });
            });

            this.tree.on('render', () => {
                this.panelWrapper.classList.remove('overlay-loading');

                if (this.tree.children.length === 0) {
                    this.tree.destroy();
                    this.panelWrapper.querySelector(
                        '#js-app__models-panel__tree-wrapper'
                    ).innerHTML = `<div class="panel-no-items"><i class="iki iki-models"></i><br>${kuura.getMessage('js_logpoints.no_models')}</div>`;
                } else {
                    if (this.layers.alignments.activeAlignmentFeature) {
                        this.checkItem(this.layers.alignments.getActiveAlignment().modelId);
                    }
                }

                FileTreeService.mergeTreeStates(this.preloadTree, this.tree);
                if (this.stateful.queuedRestoreState) {
                    this.stateful.restoreState(this.stateful.queuedRestoreState);
                }
                if (this.queueFocusIdOrUuid) {
                    this.focusItem(this.queueFocusIdOrUuid);
                    this.queueFocusIdOrUuid = null;
                }

                setTimeout(() => this.renderSelectionFeedback(), 50);
            });

            this.tree.initAndRender(treeData, true);
        });
    }

    handleUncheckedFile = (node: TreeNode) => {
        node.setChecked(0, true, false);
        this.layers.pipes.removeFeaturesBySource({ modelId: node.id });
        this.layers.alignments.removeFeaturesBySource({ modelId: node.id });
        this.layers.points.removeFeaturesBySource({
            modelId: node.id,
            modelName: node.text,
        });

        this.layers.tekPoints?.removeFeaturesBySource({
            modelId: node.id,
            modelName: node.text,
        });

        this.layers.stringlines.removeFeaturesBySource({
            modelId: node.id,
            modelName: node.text,
        });

        this.layers.models.removeFeaturesBySource({
            modelId: node.id,
            modelName: node.text,
        });

        const model = this.activeModels.find(m => m.uuid === node.uuid);

        if (model) {
            model.dispose();
            this.activeModels = this.activeModels.filter(m => m.uuid !== model.uuid);
        }
    };

    handleCheckedFileNode = async (node: TreeNode): Promise<any> => {
        if (!node.ajaxData.hasOwnProperty('layerCounts')) {
            try {
                console.log('Checked model has no layerCounts');
                const modelInfo = await ModelService.getModelInfoByHeadUuid(node.headUuid);
                node.ajaxData.layerCounts = modelInfo.layerCounts;
                // at this point node.id/uuid/headUuid are all == headUuid, because it is only thing available during preload
                node.ajaxData.uuid = modelInfo.uuid;
                node.text = modelInfo.name;
                node.uuid = modelInfo.uuid;
                node.id = modelInfo.id;
            } catch {
                console.warn('failed to fetch model info by headuuid', node);
                return;
            }
        }

        const counts: LayerCounts = node.ajaxData.layerCounts;
        if (!counts) return;

        try {
            node.set('loading', true);

            if (counts.alignments > 0) {
                await this.layers.alignments.fetchAndDrawFile(node.id);
            }

            if (counts.stringlines || counts.breaklineLayers || counts.features || counts.pointsLayers) {
                const response = await this.layers.models.fetchFile(node.id);

                // Check if this is a TEK file and route to correct layer
                if (response.outlines.props && response.outlines.props.name === 'tek') {
                    this.layers.tekPoints?.drawAll(response.outlines.points); // Use TEK layer
                } else {
                    this.layers.points.drawAll(response.outlines.points); // Use GT layer
                }
                const realPoints = response.outlines.points.map(p => p.lon + '_' + p.lat);
                const stringlines = this.layers.stringlines.drawAllFromOutlineLayers(response.outlines.layers, node.ajaxData.name);

                if (counts.triangleMeshes === 0) {
                    // Model does not have triangle meshes
                    // so Viz API does not return outlines.
                    // In this case we draw all the layers we get from Kuura

                    // We dont want both outlines from kuura and from Viz API
                    // to be drawn on the map at the same time

                    this.layers.models.drawAll(response.outlines.layers);
                }

                // draw fake points for at the start/end of stringlines for showing points labels on map
                // check if real point already exists because gt files can contain both lines and points at the end of lines
                const fakePoints: IOutlinePoint[] = stringlines
                    .filter(s => !s.solid)
                    .flatMap(sl => [sl.points[0], sl.points.pop()])
                    .filter(p => realPoints.indexOf(p.lon + '_' + p.lat) === -1)
                    .map(p => {
                        const en = latLonToEN(p.lat, p.lon);
                        const op: IOutlinePoint = {
                            sourceModelId: node.id,
                            isLinePoint: true,
                            lat: p.lat,
                            lon: p.lon,
                            elevation: p.elevation,
                            easting: en[0],
                            northing: en[1],
                            filename: node.ajaxData.name,
                            code: null,
                            valid: true,
                            props: null,
                        };
                        return op;
                    });
                const res = this.layers.points.drawAll(fakePoints);
                res.features.forEach(f => f.set('hidden', true));
            }

            if (counts.triangleMeshes > 0) {
                if (this.modelsInLoading.has(node.id)) {
                    return;
                }

                this.modelsInLoading.add(node.id);

                node.setError(null);

                const model = new Model(node, this.layers.structures, this.layers.models);
                await model.load();
                this.activeModels.push(model);

                node.root.calculateCheckboxes();

                this.modelsInLoading.delete(node.id);
            }

            if (counts.pipeNetworks > 0) {
                await this.layers.pipes.fetchAndDrawFile(node.id);
            }

            // 'node' might be from preload tree so check if real tree was loaded in meantime when promise resolves

            const activeNode = this.tree?.getByUuid(node.uuid) ?? node;
            activeNode.setChecked(1, true, false);
        } catch (e) {
            const activeNode = this.tree?.getByUuid(node.uuid) ?? node;
            activeNode.setChecked(0, true, false);
            activeNode.setError('Failed to load');
        } finally {
            const activeNode = this.tree?.getByUuid(node.uuid) ?? node;
            activeNode.set('loading', false);
            activeNode.root.calculateCheckboxes();
        }
    };

    readonly selectAll = () => {
        if (this.tree.getChildren(true).filter(c => c.isFile()).length > 30) {
            if (!confirm(kuura.getMessage('general.warning.loading_many_items') + kuura.getMessage('general.warning.slow_crashing_action'))) {
                return;
            }
        }

        this.tree.each(child => {
            if (!child.isFolder()) {
                const counts: LayerCounts = child?.ajaxData?.layerCounts;

                if (counts && countsIncludesModels(counts)) {
                    this.handleCheckedFileNode(child);
                }
            }
        }, true);

        setTimeout(this.renderSelectionFeedback);
    };

    public loadModelWithPreload = (headUuid: string): Promise<void> => {
        // load model on map before the main tree is loaded
        let tree: Tree = null;

        if (!headUuid) {
            console.warn('loadModelWithPreload no uuid provided');
            return;
        }

        if (this.tree) {
            tree = this.tree;
        } else {
            tree = this.preloadTree;
            if (!tree.getByHeadUuid(headUuid)) {
                tree.addChild({
                    id: headUuid,
                    uuid: headUuid,
                    headUuid: headUuid,
                    name: headUuid,
                } as any);
            }
        }

        const node = tree.getByHeadUuid(headUuid);
        node.setChecked(1, true, false);
        return this.handleCheckedFileNode(node);
    };

    private renderSelectionFeedback = () => {
        const selectionEl = document.querySelector('#map-2d-models-panel-wrapper .tree-selection-feedback-wrapper');
        if (this.tree && selectionEl) {
            const selection = this.tree.getCheckedAndUncheckedFileIds();
            ReactUtils.safeRender(
                <TreeSelectionFeedback
                    checkedCount={selection.checkedIds.length}
                    loadedItemsCount={selection.checkedIds.length}
                    totalItemsCount={selection.checkedIds.length + selection.uncheckedIds.length}
                    onClearSelection={this.clearSelection}
                    onSelectAll={this.selectAll}
                    countTooltip={kuura.getMessage('js_map.models.selected_count_tooltip')}
                />,
                selectionEl
            );
        }
    };

    stateful = {
        queuedRestoreState: null,

        defaultState: {
            headUuids: [],
            fileIds: [],
            fileNames: [],
            iconLabelProps: [],
            showLinePoints: false,
            showDeviations: false,
            layerOpacity: 100,
        },

        getCurrentState: (): IMapPageStateModelsPanel => {
            if (!this.enabled()) return null;

            return {
                headUuids: this.enabled() ? this.getCheckedHeadUuids() : [],
                fileIds: this.enabled() ? this.getCheckedFileIds() : [],
                fileNames: this.enabled() ? this.getCheckedFileNames() : [],
                iconLabelProps: this.layers.points.labelProps,
                showLinePoints: this.layers.points.showLinePoints,
                showDeviations: this.layers.points.showDeviations,
                layerOpacity: this.settings.layerOpacity,
            };
        },

        restoreDefaultState: () => {
            if (!this.enabled()) return null;
            this.stateful.restoreState(this.stateful.defaultState);
        },

        restoreState: (state: IMapPageStateModelsPanel): void => {
            if (!state || !this.enabled()) return null;

            console.log('[ model panel ] restoreState, tree is initialized', this.tree ? 'true' : 'false', state);

            if (!this.tree && this.preloadTree) {
                this.stateful.queuedRestoreState = JSON.parse(JSON.stringify(state)) as IMapPageStateModelsPanel;
                state.headUuids.forEach(this.loadModelWithPreload);
                return;
            }

            if (PageStateUtils.stateHasKey<IMapPageStateModelsPanel>(state, 'iconLabelProp')) {
                this.layers.points.setLabelProps([state.iconLabelProp]);
            }

            if (PageStateUtils.stateHasKey<IMapPageStateModelsPanel>(state, 'iconLabelProps')) {
                this.layers.points.setLabelProps(state.iconLabelProps);
            }

            if (PageStateUtils.stateHasKey<IMapPageStateModelsPanel>(state, 'layerOpacity')) {
                this.setLayerOpacity(state.layerOpacity);
            }

            if (PageStateUtils.stateHasKey<IMapPageStateModelsPanel>(state, 'showLinePoints')) {
                this.layers.points.showLinePoints = state.showLinePoints;
            }

            if (PageStateUtils.stateHasKey<IMapPageStateModelsPanel>(state, 'showDeviations')) {
                this.layers.points.setShowDeviations(state.showDeviations);
            }

            if (Array.isArray(state.headUuids) && this.preloadTree) {
                const preloadedModels = this.preloadTree.getChildren().map(c => c.headUuid);
                state.headUuids = [...state.headUuids, ...preloadedModels].filter(Boolean);
            }

            if (PageStateUtils.stateHasKey<IMapPageStateModelsPanel>(state, 'headUuids') && PageStateUtils.stateHasKey<IMapPageStateModelsPanel>(state, 'fileIds')) {
                if (Array.isArray(state.headUuids) && Array.isArray(state.fileIds)) {
                    this.tree.checkIdsOrHeadUuids(state.fileIds, state.headUuids);
                    this.tree.expandIdsOrHeadUuids(state.fileIds, state.headUuids);
                }
            }

            this.stateful.queuedRestoreState = null;
            if (this.preloadTree) {
                this.preloadTree.destroy();
                this.preloadTree = null;
            }
        },
    };
}
